import { useCallback, useMemo } from 'react';
import {
    DateTimeService,
    DateTimeUtcService,
    DecimalService,
    multiplyNumbersAsDecimals,
    useAppClockService,
    useTimezone,
} from '@mews/framework';
import { WithAccountId } from '@mews-commander/types';
import { useRepositorySuspenseQuery } from '@mews-commander/data-access';
import { MAX_PREAUTHORIZATION_EXPIRATION_DAYS } from '../../../constants';
import { useGetPayableInvoicesSuspense } from '../../InvoiceAsyncSelect/queries';
import { CommonProcessPaymentFormModel, ProcessPaymentFormField } from './ProcessPaymentFormSchema';
import { DEFAULT_PREAUTHORIZATION_EXPIRATION_DAYS } from './const';

export const useGetPreauthorizationDateTimesUtc = () => {
    const appClockService = useAppClockService();
    const nowDateTimeUtc = appClockService.getAppNowUtc();
    const preathMinDateTimeUtc = DateTimeUtcService.addDays(nowDateTimeUtc, 1);
    const preathMaxDateTimeUtc = DateTimeUtcService.addDays(nowDateTimeUtc, MAX_PREAUTHORIZATION_EXPIRATION_DAYS);
    const preathDefaultDateTimeUtc = DateTimeUtcService.addDays(
        nowDateTimeUtc,
        DEFAULT_PREAUTHORIZATION_EXPIRATION_DAYS
    );

    return {
        minDateTimeUtc: preathMinDateTimeUtc,
        maxDateTimeUtc: preathMaxDateTimeUtc,
        defaultDateTimeUtc: preathDefaultDateTimeUtc,
    };
};

export const useGetExpirationUtcWithTimeNow = () => {
    const timezone = useTimezone();
    const appClockService = useAppClockService();

    return (expirationUtc: string) => {
        const dateTimeNow = DateTimeService.fromUtc(appClockService.getAppNowUtc(), timezone);
        const timeNow = DateTimeService.toTime(dateTimeNow);

        const expirationDate = DateTimeService.toDate(DateTimeService.fromUtcString(expirationUtc, timezone));
        const expirationDateTimeWithTimeNow = DateTimeService.fromDateAndTime(expirationDate, timeNow);
        return DateTimeService.toUtc(expirationDateTimeWithTimeNow).value;
    };
};

interface GetTotalOfSelectedInvoicesProps extends WithAccountId {
    selectedInvoiceIds: CommonProcessPaymentFormModel[ProcessPaymentFormField.InvoiceIds];
    currencyCode: CommonProcessPaymentFormModel[ProcessPaymentFormField.CurrencyCode];
    ownerIds?: string[];
}

export const useGetTotalOfSelectedInvoicesSuspense = ({
    selectedInvoiceIds,
    currencyCode,
    accountId,
    ownerIds,
}: GetTotalOfSelectedInvoicesProps) => {
    const convertAmountToCurrency = useConvertAmountToCurrencySuspense();
    const hasMultipleOwners = ownerIds && ownerIds?.length > 1;
    const {
        data: { invoices },
    } = useGetPayableInvoicesSuspense({ ownerIds: hasMultipleOwners ? ownerIds : [accountId] });

    return useMemo(() => {
        if (!selectedInvoiceIds) {
            return null;
        }

        const totalFromInvoices = selectedInvoiceIds
            .map((invoiceId) => (invoices || []).find(({ id }) => invoiceId === id))
            .reduce(
                (total, invoice) =>
                    total + (invoice ? convertAmountToCurrency(invoice?.balance.value || 0, currencyCode) || 0 : 0),
                0
            );

        return DecimalService.toNumber(DecimalService.round(DecimalService.parse(totalFromInvoices), 2));
    }, [selectedInvoiceIds, invoices, convertAmountToCurrency, currencyCode]);
};

export const useConvertAmountToCurrencySuspense = (
    conversionRateMode: 'conversionRateFromDefault' | 'conversionRateToDefault' = 'conversionRateFromDefault'
) => {
    const { data: currencyAcceptances } = useRepositorySuspenseQuery('currencyAcceptances', 'getAll', {});

    return useCallback(
        (amount: number, currency: string) => {
            if (!currencyAcceptances) {
                return;
            }

            const currencyConversion = currencyAcceptances.find(
                (currencyAcceptance) => currencyAcceptance.currencyCode === currency
            );

            if (!currencyConversion) {
                return;
            }

            const conversionRate = currencyConversion[conversionRateMode];

            const rawResult = multiplyNumbersAsDecimals(amount, conversionRate);

            return DecimalService.toNumber(DecimalService.round(DecimalService.parse(rawResult), 2));
        },
        [currencyAcceptances, conversionRateMode]
    );
};
